import React from "react";
import { Link } from "react-router-dom";
import {
  Quote, ArrowRight, MapPin, Users, Building, Compass,
  Heart, Handshake, Sun,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import MoroccanPolaroid from "@/components/MoroccanPolaroid";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  hero_overline: { en: "One family, one calling", fr: "Une famille, une vocation", es: "Una familia, una vocación" },
  hero_title: {
    en: "Two men. One desert. A story that became a hotel group.",
    fr: "Deux hommes. Un désert. Une histoire devenue un groupe d'hôtels.",
    es: "Dos hombres. Un desierto. Una historia que se hizo grupo hotelero.",
  },
  hero_sub: {
    en: "Tayeb Ettaiek, a Bedouin of the Moroccan desert. Lluís Pont, a Spanish adventurer. They met by accident at a petrol station in Arfoud — and never really parted again.",
    fr: "Tayeb Ettaiek, Bédouin du désert marocain. Lluís Pont, aventurier catalan. Ils se sont rencontrés par hasard, à la pompe à essence d'Arfoud — et ne se sont jamais vraiment quittés.",
    es: "Tayeb Ettaiek, beduino del desierto marroquí. Lluís Pont, aventurero catalán. Se conocieron por casualidad en una gasolinera de Arfoud — y nunca se separaron del todo.",
  },

  who_overline: { en: "Who we are", fr: "Qui nous sommes", es: "Quiénes somos" },
  who_title: {
    en: "A family group, rooted in the south of Morocco.",
    fr: "Un groupe familial, enraciné dans le sud du Maroc.",
    es: "Un grupo familiar, enraizado en el sur de Marruecos.",
  },
  who_lead: {
    en: "Grup Xaluca is a large team — hotels, auberges and bivouacs across Morocco, a tourist-transport agency, and a travel agency in Spain. Strategically placed across the southern region, our properties welcome more than 100 000 guests every year, from every continent.",
    fr: "Grup Xaluca est une grande équipe — hôtels, auberges et bivouacs au Maroc, une agence de transport touristique et une agence de voyages en Espagne. Stratégiquement implantées dans le sud du pays, nos adresses accueillent plus de 100 000 voyageurs chaque année, venus de tous les continents.",
    es: "Grup Xaluca es un gran equipo — hoteles, albergues y bivacs en Marruecos, una agencia de transporte turístico y una agencia de viajes en España. Estratégicamente situadas en el sur del país, nuestras direcciones acogen a más de 100 000 viajeros al año, de todos los continentes.",
  },
  who_lead_2: {
    en: "Tayeb Ettaiek, Moroccan, and Lluís Pont, Spanish, lead the group — striking the balance between tradition and modernity, taking care of every detail so that every guest feels both at home and intrigued by something entirely new. Lakhlafa Ettaiek coordinates every kilometre of road, every guide, every camel — and knows the south the way a librarian knows a beloved shelf.",
    fr: "Tayeb Ettaiek, Marocain, et Lluís Pont, Espagnol, dirigent le groupe — gardant l'équilibre entre tradition et modernité, veillant à chaque détail pour que chaque voyageur se sente à la fois chez lui et intrigué par quelque chose d'entièrement neuf. Lakhlafa Ettaiek coordonne chaque kilomètre de route, chaque guide, chaque chameau — et connaît le sud comme un bibliothécaire connaît son rayon préféré.",
    es: "Tayeb Ettaiek, marroquí, y Lluís Pont, español, lideran el grupo — manteniendo el equilibrio entre tradición y modernidad, cuidando cada detalle para que cada viajero se sienta al mismo tiempo en casa y atrapado por algo completamente nuevo. Lakhlafa Ettaiek coordina cada kilómetro de carretera, cada guía, cada camello — y conoce el sur como un bibliotecario conoce su estantería favorita.",
  },
};

const STATS = [
  { value: "100k+", label: { en: "guests welcomed every year", fr: "voyageurs accueillis chaque année", es: "viajeros recibidos al año" } },
  { value: "300+", label: { en: "families employed by the group", fr: "familles employées par le groupe", es: "familias empleadas por el grupo" } },
  { value: "9", label: { en: "properties across southern Morocco", fr: "adresses dans le sud du Maroc", es: "direcciones en el sur de Marruecos" } },
  { value: "27", label: { en: "years and counting", fr: "ans, et la suite", es: "años, y los que vienen" } },
];

const FOUNDERS = [
  {
    id: "tayeb",
    name: "Tayeb Ettaiek",
    role: { en: "Co-founder · Morocco", fr: "Co-fondateur · Maroc", es: "Cofundador · Marruecos" },
    photo: "https://images.unsplash.com/photo-1542206395-9feb3edaa68d?auto=format&fit=crop&w=900&q=80",
    quote: {
      en: "“The desert taught us hospitality. The dunes never close their door.”",
      fr: "« Le désert nous a enseigné l'hospitalité. Les dunes ne ferment jamais leur porte. »",
      es: "«El desierto nos enseñó la hospitalidad. Las dunas nunca cierran su puerta.»",
    },
    bio: {
      en: "Born and raised between the palm groves of Tafilalet and the dunes of Erg Chebbi, Tayeb is the spirit of the group on Moroccan soil. He grew up running the family's Café-Restaurant Des Dunes in Arfoud and the first small guesthouse near Merzouga — the very seed of what became Grup Xaluca.",
      fr: "Né et élevé entre les palmeraies du Tafilalet et les dunes de l'Erg Chebbi, Tayeb est l'âme du groupe sur le sol marocain. Il a grandi entre le Café-Restaurant Des Dunes familial à Arfoud et la petite maison d'hôtes des débuts à Merzouga — la graine de ce qui allait devenir Grup Xaluca.",
      es: "Nacido y criado entre los palmerales del Tafilalet y las dunas de Erg Chebbi, Tayeb es el alma del grupo en suelo marroquí. Creció entre el Café-Restaurant Des Dunes familiar de Arfoud y la pequeña hospedería de los inicios cerca de Merzouga — la semilla de lo que sería Grup Xaluca.",
    },
    values: [
      { en: "Berber hospitality", fr: "Hospitalité berbère", es: "Hospitalidad bereber" },
      { en: "Local craft & makers", fr: "Artisans locaux", es: "Artesanos locales" },
      { en: "Knowledge of the desert", fr: "Connaissance du désert", es: "Conocimiento del desierto" },
    ],
  },
  {
    id: "lluis",
    name: "Lluís Pont",
    role: { en: "Co-founder · Spain", fr: "Co-fondateur · Espagne", es: "Cofundador · España" },
    photo: "https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=crop&w=900&q=80",
    quote: {
      en: "“Sometimes the things you weren't looking for find you. If you let yourself be guided by your heart, they rarely end badly.”",
      fr: "« Parfois, ce que l'on ne cherchait pas vient à votre rencontre. Et si l'on se laisse guider par le cœur, cela finit rarement mal. »",
      es: "«A veces las cosas que no buscabas te encuentran. Y si te dejas guiar por el corazón, raras veces acaban mal.»",
    },
    bio: {
      en: "Catalan adventurer, motorcyclist, dreamer-in-chief. Lluís discovered the south of Morocco on his motorbike long before Grup Xaluca existed; he returned with his wife Emma and his children Elisabet and Joan, and from that family trip on, the road to Arfoud kept calling him back.",
      fr: "Aventurier catalan, motard, rêveur en chef. Lluís a découvert le sud du Maroc à moto bien avant que Grup Xaluca n'existe ; il y est revenu avec son épouse Emma et leurs enfants Elisabet et Joan, et depuis ce voyage familial, la route d'Arfoud n'a jamais cessé de le rappeler.",
      es: "Aventurero catalán, motorista, soñador jefe. Lluís descubrió el sur de Marruecos en moto mucho antes de que existiera Grup Xaluca; volvió con su esposa Emma y sus hijos Elisabet y Joan, y desde aquel viaje familiar la carretera a Arfoud no dejó de llamarlo.",
    },
    values: [
      { en: "Service & detail", fr: "Service & détail", es: "Servicio y detalle" },
      { en: "European design standards", fr: "Standards européens du design", es: "Estándares europeos de diseño" },
      { en: "Slow travel ethic", fr: "Éthique du voyage lent", es: "Ética del viaje lento" },
    ],
  },
];

const PHILOSOPHY = [
  {
    icon: Heart,
    title: { en: "Hospitality first", fr: "L'hospitalité d'abord", es: "La hospitalidad primero" },
    text: {
      en: "Every Xaluca address starts with the same Berber gesture — three mint teas, three rounds, three different conversations. The architecture changes from one property to the next. The welcome does not.",
      fr: "Chaque adresse Xaluca commence par le même geste berbère — trois thés à la menthe, trois services, trois conversations différentes. L'architecture change d'une propriété à l'autre. L'accueil, lui, ne change jamais.",
      es: "Cada dirección Xaluca empieza con el mismo gesto bereber — tres tés a la menta, tres rondas, tres conversaciones distintas. La arquitectura cambia de una propiedad a otra. La bienvenida no.",
    },
  },
  {
    icon: Handshake,
    title: { en: "A bridge between two cultures", fr: "Un pont entre deux cultures", es: "Un puente entre dos culturas" },
    text: {
      en: "Berber tradition meets Catalan precision. Morocco brings the music, the spice, the soul. Spain brings the discipline, the design, the European eye. Neither half is louder than the other.",
      fr: "Tradition berbère et précision catalane. Le Maroc apporte la musique, l'épice, l'âme. L'Espagne apporte la discipline, le design, le regard européen. Aucune moitié ne crie plus fort que l'autre.",
      es: "Tradición bereber y precisión catalana. Marruecos aporta la música, la especia, el alma. España aporta la disciplina, el diseño, la mirada europea. Ninguna mitad grita más fuerte que la otra.",
    },
  },
  {
    icon: Sun,
    title: { en: "Of the place, for the place", fr: "Du lieu, pour le lieu", es: "Del lugar, para el lugar" },
    text: {
      en: "Adobe walls, hand-laid zellige, fossil-marble from local mines, Berber rugs woven a few valleys away. Almost nothing in our hotels comes from outside the region — and that is on purpose.",
      fr: "Murs en adobe, zellige posé main, marbre fossile des mines voisines, tapis berbères tissés à quelques vallées d'ici. Presque rien dans nos hôtels ne vient de l'extérieur de la région — et c'est volontaire.",
      es: "Muros de adobe, zellige colocado a mano, mármol fósil de las minas vecinas, alfombras bereberes tejidas a unos valles de aquí. Casi nada en nuestros hoteles viene de fuera de la región — y es a propósito.",
    },
  },
];

export const TIMELINE = [
  {
    year: "1998",
    title: "Kasbah Hotel Tombouctou",
    location: "Merzouga",
    slug: "kasbah-hotel-tombouctou",
    text: {
      en: "The Ettaiek family's first establishment — quickly became a Merzouga reference. After Grup Xaluca was born, it was fully renovated and expanded into one of the most charismatic hotels in the dunes.",
      fr: "Le tout premier établissement de la famille Ettaiek — vite devenu une référence à Merzouga. Après la naissance de Grup Xaluca, entièrement rénové et agrandi, il est aujourd'hui l'un des hôtels les plus emblématiques des dunes.",
      es: "El primer establecimiento de la familia Ettaiek — rápidamente se convirtió en una referencia en Merzouga. Tras nacer Grup Xaluca, fue completamente renovado y ampliado hasta convertirse en uno de los hoteles más carismáticos de las dunas.",
    },
  },
  {
    year: "2003",
    title: "Kasbah Hotel Xaluca",
    location: "Arfoud",
    slug: "kasbah-hotel-xaluca-arfoud",
    text: {
      en: "Twelve rooms at first — that was the dream. Twenty-two years of perseverance later, the kasbah has two hundred rooms, eight bungalows, and has become a benchmark for hospitality across the south of Morocco.",
      fr: "Douze chambres au départ — c'était le rêve. Vingt-deux ans de persévérance plus tard, la kasbah compte deux cents chambres, huit bungalows, et est devenue une référence d'hospitalité dans tout le sud du Maroc.",
      es: "Doce habitaciones al principio — ese era el sueño. Veintidós años de perseverancia después, la kasbah cuenta con doscientas habitaciones, ocho bungalows y es referente de hospitalidad en todo el sur de Marruecos.",
    },
  },
  {
    year: "2008",
    title: "Auberge La Belle Étoile",
    location: "Merzouga",
    slug: "auberge-la-belle-etoile",
    text: {
      en: "Founded to offer an exceptional night in the very heart of the Erg Chebbi dunes — now a strategic stop for groups and travellers who want to fall asleep under the Milky Way.",
      fr: "Fondée pour offrir une nuit d'exception au cœur même des dunes de l'Erg Chebbi — aujourd'hui une étape stratégique pour les groupes et les voyageurs qui veulent s'endormir sous la Voie lactée.",
      es: "Fundada para ofrecer una noche excepcional en pleno corazón de las dunas del Erg Chebbi — hoy una etapa estratégica para grupos y viajeros que quieren dormirse bajo la Vía Láctea.",
    },
  },
  {
    year: "2011",
    title: "Hotel Xaluca Dades",
    location: "Boumalne Dades",
    slug: "hotel-xaluca-dades",
    text: {
      en: "Reborn from a former National Parador of Morocco — originally designed by a renowned Japanese architect — Grup Xaluca rebuilt it in an African architectural language, perched between the Middle Atlas and the desert.",
      fr: "Renaissance d'un ancien Parador National du Maroc — conçu à l'origine par un architecte japonais reconnu — Grup Xaluca l'a rebâti dans une grammaire architecturale africaine, perché entre le Moyen Atlas et le désert.",
      es: "Renacido de un antiguo Parador Nacional de Marruecos — diseñado en origen por un reputado arquitecto japonés — Grup Xaluca lo reconstruyó en una gramática arquitectónica africana, encaramado entre el Atlas Medio y el desierto.",
    },
  },
  {
    year: "2014",
    title: "Xaluca Ifess Fess Refuge de Montagne",
    location: "Atlas Mountains",
    slug: "xaluca-ifess-fess-refuge",
    text: {
      en: "Born of the demand of daring guests asking to explore the high routes of the Middle and High Atlas. A remote refuge, reached on foot or by mule — for those who want to disappear into nature and come back changed.",
      fr: "Né de la demande d'hôtes audacieux désireux d'explorer les hautes routes du Moyen et du Haut Atlas. Un refuge isolé, atteint à pied ou à dos de mulet — pour ceux qui veulent se perdre dans la nature et en revenir transformés.",
      es: "Nacido de la demanda de huéspedes audaces que querían explorar las altas rutas del Atlas Medio y Alto. Un refugio aislado, al que se llega a pie o a lomos de mula — para quienes quieren perderse en la naturaleza y volver transformados.",
    },
  },
  {
    year: "2016",
    title: "Arfoud Voyages",
    location: "Arfoud",
    // No matching hotel — use a thematic image of the Saharan road
    fallbackImage:
      "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?auto=format&fit=crop&w=1200&q=80",
    text: {
      en: "Our wholesale travel agency — built to compose Morocco-wide journeys for our long-time partners, taking the chaos of logistics off their shoulders.",
      fr: "Notre agence de voyages réceptive — pensée pour composer des itinéraires à travers tout le Maroc pour nos partenaires historiques, et leur retirer le poids de la logistique.",
      es: "Nuestra agencia receptiva — pensada para componer itinerarios por todo Marruecos para nuestros socios históricos, y quitarles de los hombros el peso de la logística.",
    },
  },
  {
    year: "2017",
    title: "Xaluca Tours",
    location: "Sabadell · Barcelona",
    // European desk — use a warm Catalan-coastline / Mediterranean motif
    fallbackImage:
      "https://images.unsplash.com/photo-1583422409516-2895a77efded?auto=format&fit=crop&w=1200&q=80",
    text: {
      en: "A retail travel agency in Catalonia, specialised in tailor-made Moroccan journeys — cultural, adventure, luxury, romantic. The European desk of Grup Xaluca, run by people who actually live in Morocco several months a year.",
      fr: "Une agence de voyages en boutique en Catalogne, spécialisée dans les itinéraires marocains sur mesure — culturels, aventureux, de luxe, romantiques. Le bureau européen de Grup Xaluca, tenu par des gens qui vivent au Maroc plusieurs mois par an.",
      es: "Una agencia de viajes minorista en Cataluña, especializada en itinerarios marroquíes a medida — culturales, aventureros, de lujo, románticos. El despacho europeo de Grup Xaluca, llevado por gente que vive en Marruecos varios meses al año.",
    },
  },
  {
    year: "2020",
    title: "Xaluca Aït Ben Haddou Maison d'Hôte",
    location: "Aït Ben Haddou",
    slug: "xaluca-ait-ben-haddou",
    text: {
      en: "A boutique guesthouse inside the UNESCO-listed ksar — a rare permission, a quiet building, a chance to sleep inside one of the most filmed villages on Earth.",
      fr: "Une maison d'hôtes boutique au cœur du ksar classé UNESCO — une autorisation rare, un bâti silencieux, l'occasion de dormir au sein de l'un des villages les plus filmés au monde.",
      es: "Una hospedería boutique en el corazón del ksar declarado UNESCO — un permiso poco común, una construcción silenciosa, la ocasión de dormir dentro de uno de los pueblos más filmados del mundo.",
    },
  },
  {
    year: "2023",
    title: "Hotel Xaluca Spa Aguelmame Sidi Ali",
    location: "Lake Aguelmame Sidi Ali",
    slug: "hotel-xaluca-spa-aguelmame-sidi-ali",
    text: {
      en: "An old hunting lodge on the shore of Morocco's deepest lake, completely rebuilt by Grup Xaluca and a team of master cedar carvers from the surrounding villages — thirty-two rooms in cedar, a heated pool, a high-altitude spa.",
      fr: "Un ancien pavillon de chasse au bord du lac le plus profond du Maroc, entièrement reconstruit par Grup Xaluca et des maîtres ébénistes en cèdre des villages alentour — trente-deux chambres en cèdre, piscine chauffée, spa d'altitude.",
      es: "Un antiguo pabellón de caza a orillas del lago más profundo de Marruecos, totalmente reconstruido por Grup Xaluca y maestros ebanistas en cedro de las aldeas vecinas — treinta y dos habitaciones en cedro, piscina climatizada, spa de altura.",
    },
  },
  {
    year: "2024",
    title: "Riad Xaluca Merzouga",
    location: "Merzouga",
    slug: "hotel-riad-xaluca",
    tag: { en: "New project", fr: "Nouveau projet", es: "Nuevo proyecto" },
    text: {
      en: "A boutique riad next to the Erg Chebbi dunes — infinity pool over the sand, panoramic restaurant, local guides who organise sunrise excursions. The most intimate Xaluca address yet.",
      fr: "Un riad boutique aux portes des dunes de l'Erg Chebbi — piscine à débordement sur le sable, restaurant panoramique, guides locaux pour les excursions au lever du soleil. La plus intime des adresses Xaluca à ce jour.",
      es: "Un riad boutique a las puertas de las dunas del Erg Chebbi — piscina infinita sobre la arena, restaurante panorámico, guías locales para las salidas al amanecer. La dirección Xaluca más íntima hasta la fecha.",
    },
  },
  {
    year: "Soon",
    title: "Hotel Xaluca Dunes",
    location: "Merzouga",
    slug: "hotel-xaluca-dunes",
    tag: { en: "Under construction", fr: "En construction", es: "En construcción" },
    text: {
      en: "Our five-star desert flagship, currently under construction at the edge of Erg Chebbi.",
      fr: "Notre vaisseau cinq étoiles du désert, en cours de construction à la lisière de l'Erg Chebbi.",
      es: "Nuestro buque insignia cinco estrellas en el desierto, en construcción en el borde de Erg Chebbi.",
    },
  },
];

// The full Lluís Pont letter — preserved with the original warmth.
const LETTER = {
  intro: {
    en: "Everything began on a trip I did with my wife, Emma, and my children, Elisabet and Joan, to Morocco. I wanted them to discover a country that, in my previous visits with my motorbike, impressed me — especially because of its people, who are welcoming and kind.",
    fr: "Tout a commencé lors d'un voyage que j'ai fait avec ma femme Emma et mes enfants Elisabet et Joan, au Maroc. Je voulais qu'ils découvrent un pays qui, lors de mes visites précédentes à moto, m'avait marqué — surtout par ses habitants, accueillants et bienveillants.",
    es: "Todo empezó en un viaje que hice con mi mujer, Emma, y mis hijos, Elisabet y Joan, a Marruecos. Quería que descubrieran un país que, en mis visitas anteriores en moto, me había impactado — sobre todo por su gente, acogedora y amable.",
  },
  paragraphs: [
    {
      en: "We arrived in Arfoud — called Erfoud at the time, and nicknamed “The door to the desert.” At the gas station, a cheerful young man put fuel in our vehicle. With a clumsy yet humorous attempt to speak Spanish, he asked me where we were going. When we told him we were going towards Merzouga to see the Erg Chebbi “desert of dunes,” he offered to accompany us, saying he knew the area well — his family had the Café-Restaurant Des Dunes in Arfoud and a small hostel near Merzouga.",
      fr: "Nous sommes arrivés à Arfoud — Erfoud à l'époque, surnommée « la porte du désert ». À la pompe à essence, un jeune homme enjoué a fait le plein. Dans un espagnol maladroit mais drôle, il m'a demandé où nous allions. Quand je lui ai dit que nous montions vers Merzouga voir l'Erg Chebbi, le « désert de dunes », il s'est proposé de nous accompagner ; il connaissait bien la zone — sa famille tenait le Café-Restaurant Des Dunes à Arfoud et une petite auberge près de Merzouga.",
      es: "Llegamos a Arfoud — entonces se llamaba Erfoud, apodada «la puerta del desierto». En la gasolinera, un joven alegre nos puso combustible. Con un español torpe pero divertido, me preguntó adónde íbamos. Cuando le dije que íbamos hacia Merzouga, a ver el Erg Chebbi, el «desierto de dunas», se ofreció a acompañarnos; conocía bien la zona — su familia tenía el Café-Restaurant Des Dunes de Arfoud y una pequeña hospedería cerca de Merzouga.",
    },
    {
      en: "We had no interest in being guided. I had already driven the route, and the boy was young and a stranger. He insisted a lot — and with his smile, he convinced us. He jumped into the car without even telling his family that he would be away for the night.",
      fr: "Nous n'avions aucune envie d'être guidés — j'avais déjà fait la route, et ce garçon était jeune et inconnu. Il a beaucoup insisté — et avec son sourire, il nous a convaincus. Il a sauté dans la voiture sans même prévenir sa famille qu'il s'absentait pour la nuit.",
      es: "No teníamos interés en que nos guiaran — yo ya había hecho la ruta, y el chico era joven y desconocido. Insistió mucho — y con su sonrisa, nos convenció. Se subió al coche sin avisar siquiera a su familia de que se ausentaba esa noche.",
    },
    {
      en: "During the trip, he offered to organise a walk by dromedary around the dunes, and we accepted. Once we were on the dromedaries, about to head out with a nomad guiding the animals, the young man asked me for the keys to my car. I thought he had forgotten something inside, and I threw them to him. I was stunned when I saw him drive off — disappearing with all our belongings in the vehicle. Emma had a nervous breakdown; she thought the car had been stolen in front of us.",
      fr: "En route, il a proposé d'organiser une balade en dromadaire autour des dunes ; nous avons accepté. Une fois sur les dromadaires, sur le point de partir avec un nomade qui guidait les bêtes, le jeune homme m'a demandé les clés de la voiture. J'ai cru qu'il avait oublié quelque chose à l'intérieur, et je les lui ai lancées. J'ai été stupéfait de le voir démarrer — et disparaître avec toutes nos affaires dans le véhicule. Emma a fait une crise de nerfs ; elle pensait que la voiture venait d'être volée sous nos yeux.",
      es: "Durante el trayecto, propuso organizarnos un paseo en dromedario por las dunas; aceptamos. Una vez subidos a los dromedarios, a punto de salir con un nómada que los guiaba, el joven me pidió las llaves del coche. Pensé que se había olvidado algo dentro, y se las lancé. Me quedé atónito al verlo arrancar — y desaparecer con todas nuestras cosas en el vehículo. Emma sufrió una crisis de nervios; pensaba que el coche acababa de ser robado delante de nuestros ojos.",
    },
    {
      en: "I tried to communicate with the nomad who owned the dromedaries, but he did not understand a word I was saying. His only reply was a big smile.",
      fr: "J'ai tenté de m'expliquer avec le nomade propriétaire des dromadaires — il ne comprenait pas un mot. Sa seule réponse a été un grand sourire.",
      es: "Intenté hablar con el nómada dueño de los dromedarios — no entendía una palabra. Su única respuesta fue una gran sonrisa.",
    },
    {
      en: "We spent three hours riding the dromedaries through the dunes until we reached a settlement of people from Black Africa called “Hamelia.” The first thing I saw was my car — washed and in perfect condition. What the young man had done was take the car to the end of the route and wash it, hoping for a tip. The problem was that he had not told us before what he was doing.",
      fr: "Nous avons passé trois heures à dos de dromadaire à travers les dunes, jusqu'à un village d'Afrique noire appelé « Hamelia ». La première chose que j'ai vue, c'est ma voiture — lavée et impeccable. Le jeune homme l'avait conduite jusqu'au bout de la route et l'avait lavée, dans l'espoir d'un pourboire. Le problème, c'est qu'il ne nous l'avait pas dit avant.",
      es: "Pasamos tres horas a lomos de los dromedarios atravesando las dunas, hasta llegar a un poblado de África negra llamado «Hamelia». Lo primero que vi fue mi coche — lavado y en perfecto estado. Lo que el joven había hecho era llevar el coche hasta el final de la ruta y lavarlo, con la esperanza de una propina. El problema fue que no nos lo había contado antes.",
    },
    {
      en: "The Hamelian people treated us in an extraordinary way; they made us feel unique and gave us sensations I cannot really explain. To thank them for everything they did for us, we decided to come back the next year with more people, school materials, clothes and other gifts for the families — and we contacted the young man from the petrol station again. His name was Tayeb.",
      fr: "Les habitants d'Hamelia nous ont accueillis d'une manière extraordinaire ; ils nous ont fait sentir uniques, ils nous ont donné des sensations que je ne saurais expliquer. Pour les remercier, nous avons décidé de revenir l'année suivante avec plus de gens, du matériel scolaire, des vêtements et d'autres présents pour les familles — et nous avons recontacté le jeune homme de la pompe à essence. Il s'appelait Tayeb.",
      es: "La gente de Hamelia nos trató de un modo extraordinario; nos hicieron sentir únicos, nos transmitieron sensaciones que aún no sé explicar del todo. Para agradecérselo, decidimos volver al año siguiente con más gente, material escolar, ropa y otros regalos para las familias — y volvimos a contactar al joven de la gasolinera. Se llamaba Tayeb.",
    },
    {
      en: "Every Easter, we returned to that part of the country with more friends and family, to share the experience. My relationship with Tayeb became that of lifelong friends. One day, he offered me the chance to build a small hotel with a handful of rooms on the outskirts of Arfoud — a kind of symbolic contribution to a place that had given my family and me so much hospitality, and where Tayeb had spent his whole life.",
      fr: "Chaque Pâques, nous repartions dans cette région avec davantage d'amis et de famille, pour partager l'expérience. Mon lien avec Tayeb est devenu celui d'amis pour la vie. Un jour, il m'a proposé de construire un petit hôtel avec quelques chambres à la sortie d'Arfoud — une sorte de contribution symbolique à un lieu qui nous avait offert tant d'hospitalité, à ma famille et à moi, et où Tayeb avait toujours vécu.",
      es: "Cada Pascua, volvíamos a esa parte del país con más amigos y familia, para compartir la experiencia. Mi relación con Tayeb se convirtió en una amistad de toda la vida. Un día, me propuso construir un pequeño hotel con unas pocas habitaciones a las afueras de Arfoud — una especie de aportación simbólica a un lugar que nos había dado tanta hospitalidad, a mi familia y a mí, y donde Tayeb había vivido toda su vida.",
    },
    {
      en: "And unwittingly, without thinking, letting ourselves be guided by the magic of the country and the affection of its people, today we have a large group of companies and hotels that is an excellent resource for more than three hundred families.",
      fr: "Et sans s'en rendre compte, sans réfléchir, en se laissant guider par la magie du pays et l'affection de ses habitants, nous avons aujourd'hui un grand groupe d'entreprises et d'hôtels — qui fait vivre plus de trois cents familles.",
      es: "Y sin darnos cuenta, sin pensarlo, dejándonos llevar por la magia del país y el cariño de su gente, hoy tenemos un gran grupo de empresas y hoteles — que sostiene a más de trescientas familias.",
    },
    {
      en: "None of this would have been possible if Tayeb and his entire family were not extraordinary people — with huge hearts and an outstanding sense of hospitality — who made us want to show Morocco to others.",
      fr: "Rien de tout cela n'aurait été possible si Tayeb et toute sa famille n'étaient pas des personnes extraordinaires — au grand cœur, au sens inégalé de l'hospitalité — qui nous ont donné envie de montrer le Maroc à d'autres.",
      es: "Nada de esto habría sido posible si Tayeb y toda su familia no fueran personas extraordinarias — de gran corazón, con un sentido excepcional de la hospitalidad — que nos transmitieron las ganas de enseñar Marruecos a los demás.",
    },
  ],
  outro: {
    en: "Sometimes the things you weren't looking for find you. If you let yourself be guided by your heart, they rarely end badly.",
    fr: "Parfois, ce que l'on ne cherchait pas vient à votre rencontre. Et si l'on se laisse guider par le cœur, cela finit rarement mal.",
    es: "A veces las cosas que no buscabas te encuentran. Y si te dejas guiar por el corazón, raras veces acaban mal.",
  },
  signature: "— Lluís Pont",
};

const AboutPage = () => {
  const { lang } = useLanguage();
  return (
    <div data-testid="page-about" className="bg-[#FBF6EE]/60">
      {/* HERO — multi-slide (video first, then heritage photography) */}
      <section data-testid="about-hero">
        <PageHero
          images={[
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
          ]}
          alt="About · Grup Xaluca"
          scope="page.about"
          overline={pick(COPY.hero_overline, lang)}
          title={pick(COPY.hero_title, lang)}
          sub={pick(COPY.hero_sub, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "about-who", label: { en: "Who we are", fr: "Qui nous sommes", es: "Quiénes" } },
          { id: "about-founders", label: { en: "Founders", fr: "Fondateurs", es: "Fundadores" } },
          { id: "about-philosophy", label: { en: "Philosophy", fr: "Philosophie", es: "Filosofía" } },
          { id: "about-letter", label: { en: "Letter", fr: "Lettre", es: "Carta" } },
          { id: "about-timeline", label: { en: "History", fr: "Histoire", es: "Historia" } },
          { id: "about-press", label: { en: "Press", fr: "Presse", es: "Prensa" } },
          { id: "about-cta", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* WHO WE ARE */}
      <section id="about-who" className="py-24 md:py-32 scroll-mt-32" data-testid="about-who">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="overline">{pick(COPY.who_overline, lang)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick(COPY.who_title, lang)}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-7">
            <p className="font-serif-x text-2xl md:text-3xl leading-[1.35] text-[#2C2621]">
              {pick(COPY.who_lead, lang)}
            </p>
            <p className="text-lg leading-relaxed text-[#5C5248]">
              {pick(COPY.who_lead_2, lang)}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#E8DFD2] border border-[#E8DFD2] rounded-3xl overflow-hidden mt-10">
              {STATS.map((s, i) => (
                <div key={`stat-${s.value}`} className="bg-white p-6" data-testid={`about-stat-${i}`}>
                  <p className="font-serif-x text-3xl md:text-4xl text-[#C16542] leading-none">{s.value}</p>
                  <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-[#5C5248] leading-snug">
                    {pick(s.label, lang)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section id="about-founders" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="about-founders">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-7">
              <p className="overline !text-[#D4A373]">
                {pick({ en: "Founders", fr: "Fondateurs", es: "Fundadores" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick({
                  en: "Tayeb & Lluís — and the petrol-station meeting that changed both of their lives.",
                  fr: "Tayeb & Lluís — et la rencontre, à une pompe à essence, qui a changé leur vie à tous les deux.",
                  es: "Tayeb y Lluís — y el encuentro, en una gasolinera, que cambió la vida de ambos.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5 md:pt-3">
              <p className="text-base text-[#FDFBF7]/75 leading-relaxed">
                {pick({
                  en: "Two families — Ettaiek and Pont — and the simple promise that the south of Morocco should welcome travellers the way it once welcomed them.",
                  fr: "Deux familles — Ettaiek et Pont — et une promesse simple : que le sud du Maroc accueille les voyageurs comme il les a accueillis eux-mêmes.",
                  es: "Dos familias — Ettaiek y Pont — y una promesa sencilla: que el sur de Marruecos acoja a los viajeros como una vez les acogió a ellos.",
                }, lang)}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {FOUNDERS.map((f) => (
              <article
                key={f.id}
                className="bg-[#FDFBF7]/[0.04] border border-[#FDFBF7]/15 rounded-3xl overflow-hidden"
                data-testid={`founder-${f.id}`}
                data-cms-scope={`founder.${f.id}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={f.photo}
                    alt={f.name}
                    className="w-full h-full object-cover" loading="lazy" decoding="async"
                    data-cms-role="portrait"/>
                </div>
                <div className="p-7 md:p-9">
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#D4A373]">
                    {pick(f.role, lang)}
                  </p>
                  <h3 className="font-serif-x text-3xl md:text-4xl mt-2 leading-tight text-[#FDFBF7]" data-testid={`founder-${f.id}-name`}>
                    {f.name}
                  </h3>
                  <Quote className="w-6 h-6 mt-6 text-[#D4A373]" strokeWidth={1.5} />
                  <p className="mt-3 font-serif-x italic text-xl md:text-2xl leading-snug text-[#FDFBF7]/95">
                    {pick(f.quote, lang)}
                  </p>
                  <p className="mt-6 text-[15px] leading-relaxed text-[#FDFBF7]/75">
                    {pick(f.bio, lang)}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {f.values.map((v, i) => (
                      <span
                        key={`founder-${f.id}-value-${v.en}`}
                        className="text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 rounded-full border border-[#FDFBF7]/25 text-[#FDFBF7]/85"
                      >
                        {pick(v, lang)}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section id="about-philosophy" className="py-20 md:py-24 bg-[#F2EBE1]/40 scroll-mt-32" data-testid="about-philosophy">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-center">
            {pick({ en: "Our philosophy", fr: "Notre philosophie", es: "Nuestra filosofía" }, lang)}
          </p>
          <h2 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight text-[#2C2621] text-center max-w-3xl mx-auto">
            {pick({
              en: "Three quiet rules that hold the group together.",
              fr: "Trois règles discrètes qui tiennent le groupe ensemble.",
              es: "Tres reglas discretas que mantienen el grupo unido.",
            }, lang)}
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-px bg-[#E8DFD2] border border-[#E8DFD2] rounded-3xl overflow-hidden">
            {PHILOSOPHY.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={`philosophy-${p.title.en}`} className="bg-white p-8 md:p-10" data-testid={`philosophy-${i}`}>
                  <Icon className="w-6 h-6 text-[#C16542]" strokeWidth={1.5} />
                  <h3 className="font-serif-x text-2xl mt-5 leading-tight text-[#2C2621]">
                    {pick(p.title, lang)}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#5C5248]">
                    {pick(p.text, lang)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE LETTER — full Lluís Pont story */}
      <section id="about-letter" className="py-24 md:py-32 scroll-mt-32" data-testid="about-letter">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="overline">
            {pick({ en: "An editorial letter", fr: "Une lettre éditoriale", es: "Una carta editorial" }, lang)}
          </p>
          <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
            {pick({
              en: "The beginning of a long adventure.",
              fr: "Le début d'une longue aventure.",
              es: "El principio de una larga aventura.",
            }, lang)}
          </h2>
          <p className="mt-3 text-[11px] tracking-[0.3em] uppercase text-[#C16542]">
            {pick({
              en: "Written by Lluís Pont · co-founder",
              fr: "Écrit par Lluís Pont · co-fondateur",
              es: "Escrito por Lluís Pont · cofundador",
            }, lang)}
          </p>

          <div className="mt-12 space-y-7 text-lg md:text-[19px] leading-[1.85] text-[#3C342D] font-serif-x">
            <p
              className="first-letter:font-serif-x first-letter:text-7xl first-letter:font-normal first-letter:text-[#C16542] first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:mt-1"
              data-testid="letter-intro"
            >
              {pick(LETTER.intro, lang)}
            </p>
            {LETTER.paragraphs.map((p, i) => (
              <p key={`letter-p-${i}-${(p.en || "").slice(0, 24)}`} data-testid={`letter-paragraph-${i}`}>
                {pick(p, lang)}
              </p>
            ))}
          </div>

          {/* Pull-quote outro */}
          <figure className="mt-14 border-l-2 border-[#C16542] pl-7 py-2" data-testid="letter-outro">
            <Quote className="w-6 h-6 text-[#C16542]" strokeWidth={1.5} />
            <blockquote className="mt-3 font-serif-x italic text-2xl md:text-3xl leading-snug text-[#2C2621]">
              {pick(LETTER.outro, lang)}
            </blockquote>
            <figcaption className="mt-5 text-[11px] tracking-[0.3em] uppercase text-[#5C5248]">
              {LETTER.signature}
            </figcaption>
          </figure>
        </div>
      </section>

      {/* TIMELINE */}
      <section id="about-timeline" className="py-24 md:py-32 bg-[#F2EBE1]/40 scroll-mt-32" data-testid="about-timeline">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline">
                {pick({ en: "Grup Xaluca timeline", fr: "La chronologie Grup Xaluca", es: "Cronología Grup Xaluca" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "Since 1998 — and still building.",
                  fr: "Depuis 1998 — et nous construisons encore.",
                  es: "Desde 1998 — y aún construyendo.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base leading-relaxed text-[#5C5248]">
                {pick({
                  en: "From a twelve-room dream in Arfoud to ten properties across the desert, the Atlas and Aït Ben Haddou — each address has its own story, its own architect, its own crew of local makers.",
                  fr: "D'un rêve de douze chambres à Arfoud à dix propriétés dans le désert, l'Atlas et Aït Ben Haddou — chaque adresse a son histoire, son architecte, son équipe d'artisans locaux.",
                  es: "De un sueño de doce habitaciones en Arfoud a diez propiedades en el desierto, el Atlas y Aït Ben Haddou — cada dirección tiene su historia, su arquitecto, su equipo de artesanos locales.",
                }, lang)}
              </p>
            </div>
          </div>

          <ol className="relative border-l border-[#C16542]/30 ml-3 md:ml-6 space-y-12">
            {TIMELINE.map((t, i) => {
              // Resolve the photo: prefer a real hotel image, otherwise use
              // the entry's thematic fallback. Alternating tilts give the
              // strip a soft handcrafted rhythm without breaking alignment.
              const hotelMatch = t.slug ? HOTELS.find((h) => h.slug === t.slug) : null;
              const polaroidImage = hotelMatch?.image || t.fallbackImage;
              const tilt = i % 2 === 0 ? -1.6 : 1.6;
              return (
              <li
                key={`timeline-${t.year}-${t.title}`}
                className="pl-8 md:pl-12 relative"
                data-testid={`timeline-${i}`}
              >
                <span
                  className="absolute -left-[7px] top-2 w-3.5 h-3.5 rounded-full bg-[#C16542] ring-4 ring-[#F2EBE1]"
                  aria-hidden="true"
                />
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  <div className="lg:col-span-2">
                    <p className="font-serif-x text-3xl md:text-4xl text-[#C16542] leading-none">{t.year}</p>
                  </div>
                  <div className="lg:col-span-6">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <h3 className="font-serif-x text-2xl md:text-3xl text-[#2C2621] leading-tight">
                        {t.title}
                      </h3>
                      <p className="text-[11px] tracking-[0.3em] uppercase text-[#5C5248] flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-[#C16542]" strokeWidth={1.6} />
                        {t.location}
                      </p>
                      {t.tag && (
                        <span className="text-[10px] tracking-[0.25em] uppercase px-2.5 py-1 rounded-full bg-[#C16542]/10 text-[#C16542]">
                          {pick(t.tag, lang)}
                        </span>
                      )}
                    </div>
                    <p className="mt-4 text-[15px] md:text-base leading-relaxed text-[#3C342D] max-w-3xl">
                      {pick(t.text, lang)}
                    </p>
                  </div>
                  {/* Moroccan-inspired photo strip for each property. Hidden if
                      no image is available for the entry. */}
                  {polaroidImage && (
                    <div className="lg:col-span-4 flex justify-center lg:justify-end">
                      <MoroccanPolaroid
                        image={polaroidImage}
                        year={t.year}
                        title={t.title}
                        location={t.location}
                        tilt={tilt}
                        tag={t.tag ? pick(t.tag, lang) : undefined}
                        testid={`timeline-polaroid-${i}`}
                        scope={`about.timeline.${t.slug || i}`}
                      />
                    </div>
                  )}
                </div>
              </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* PRESS & ENQUIRIES — guides journalists, partners, and publications to the dedicated /press page */}
      <section id="about-press" className="py-20 md:py-28 bg-[#F2EBE1]/40 scroll-mt-32" data-testid="about-press">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end">
            <div className="md:col-span-7">
              <p className="overline">
                {pick({ en: "Press & enquiries", fr: "Presse & demandes", es: "Prensa y consultas" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "For journalists, partners and travel publications.",
                  fr: "Pour les journalistes, partenaires et publications de voyage.",
                  es: "Para periodistas, socios y publicaciones de viajes.",
                }, lang)}
              </h2>
              <p className="mt-6 text-lg text-[#5C5248] leading-relaxed max-w-2xl">
                {pick({
                  en: "Grup Xaluca has been written about, filmed in and recommended by some of the most thoughtful travel publications in the world. Our dedicated Press page gathers every external article, interview and media mention in one place — and is where to start any editorial conversation with our team.",
                  fr: "Grup Xaluca a été raconté, filmé et recommandé par certaines des publications de voyage les plus exigeantes au monde. Notre page Presse rassemble en un seul endroit chaque article, entretien et mention médiatique externe — c'est par là que commence toute conversation éditoriale avec notre équipe.",
                  es: "Sobre Grup Xaluca han escrito, filmado y recomendado algunas de las publicaciones de viajes más exigentes del mundo. Nuestra página de Prensa reúne en un solo lugar cada artículo, entrevista y mención mediática externa — es por ahí donde empieza cualquier conversación editorial con nuestro equipo.",
                }, lang)}
              </p>
            </div>
            <div className="md:col-span-5 md:flex md:justify-end">
              <Link
                to="/press"
                className="inline-flex items-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-5 text-xs tracking-[0.25em] uppercase transition-colors"
                data-testid="about-press-cta"
              >
                {pick({ en: "Visit the press page", fr: "Voir la page presse", es: "Ver la página de prensa" }, lang)}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section id="about-cta" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Compass className="w-8 h-8 mx-auto text-[#D4A373]" strokeWidth={1.5} />
          <h2 className="font-serif-x mt-6 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {pick({
              en: "Come and meet us. The mint tea is already on.",
              fr: "Venez à notre rencontre. Le thé à la menthe est déjà servi.",
              es: "Venga a conocernos. El té a la menta ya está servido.",
            }, lang)}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-[#FDFBF7]/75 leading-relaxed">
            {pick({
              en: "Choose a kasbah, a bivouac or a refuge — write to our concierge, and we will compose the south of Morocco around you.",
              fr: "Choisissez une kasbah, un bivouac ou un refuge — écrivez à notre conciergerie, et nous composerons le sud du Maroc autour de vous.",
              es: "Elija una kasbah, un bivac o un refugio — escriba a nuestra conserjería, y compondremos el sur de Marruecos a su medida.",
            }, lang)}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/hotels"
              className="inline-flex items-center justify-center gap-2 bg-[#C16542] text-[#FDFBF7] px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#D4A373] hover:text-[#1A1513] transition-colors"
              data-testid="about-cta-hotels"
            >
              {pick({ en: "Discover our hotels", fr: "Découvrir nos hôtels", es: "Descubrir nuestros hoteles" }, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 border border-[#FDFBF7]/40 px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors"
              data-testid="about-cta-booking"
            >
              {pick({ en: "Send a booking request", fr: "Envoyer une demande", es: "Enviar una solicitud" }, lang)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
